import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Ship, Car, Clock, MapPin, ArrowRight, Anchor, Info } from "lucide-react";

export default function GettingHerePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO
        title="How to Get to Long Island, Maine | Ferry & Travel Guide | Chestnut Hill Inn"
        description="Complete travel guide to Long Island, Maine. Ferry schedules from Portland, parking information, and tips for your island journey to Chestnut Hill Inn."
      />

      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm text-[#78716C] hover:text-[#1C1917]">About</Link>
            <Link to="/beaches" className="text-sm text-[#78716C] hover:text-[#1C1917]">Beaches</Link>
            <Link to="/getting-here" className="text-sm text-[#1C1917] font-medium">Getting Here</Link>
            <Link to="/things-to-do" className="text-sm text-[#78716C] hover:text-[#1C1917]">Things to Do</Link>
            <Link to="/winter-retreats" className="text-sm text-[#78716C] hover:text-[#1C1917]">Winter Retreats</Link>
            <Link to="/booking">
              <Button className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
                Book Now
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920"
            alt="Ferry to Long Island Maine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-16 w-full">
            <div className="flex items-center gap-2 text-white/80 mb-4">
              <Anchor size={20} />
              <span className="text-sm uppercase tracking-widest">Travel Guide</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-4">
              Getting to<br />Long Island, Maine
            </h1>
            <p className="text-white/90 text-lg max-w-xl">
              Your journey to Chestnut Hill Inn begins with a scenic ferry ride from Portland
            </p>
          </div>
        </div>
      </section>

      {/* Ferry Information */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#134E4A] text-white flex items-center justify-center">
                  <Ship size={24} />
                </div>
                <h2 className="font-serif text-3xl text-[#1C1917]">Casco Bay Lines Ferry</h2>
              </div>
              
              <p className="text-[#78716C] mb-6">
                The only way to reach Long Island is by ferry. Casco Bay Lines operates year-round service from Portland, Maine to Long Island. The ferry ride takes approximately 45 minutes and offers beautiful views of Casco Bay.
              </p>

              <div className="bg-white border border-stone-200 p-6 mb-6">
                <h3 className="font-medium text-[#1C1917] mb-4">Ferry Details</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Clock size={18} className="text-[#134E4A] mt-0.5" />
                    <span className="text-[#78716C]">Trip duration: ~45 minutes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#134E4A] mt-0.5" />
                    <span className="text-[#78716C]">Departs from: 56 Commercial St, Portland, ME</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Info size={18} className="text-[#134E4A] mt-0.5" />
                    <span className="text-[#78716C]">Runs multiple times daily, year-round</span>
                  </li>
                </ul>
              </div>

              <a 
                href="https://www.cascobaylines.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#134E4A] font-medium hover:underline"
              >
                View Current Schedule at cascobaylines.com
                <ArrowRight size={16} />
              </a>
            </div>

            <div>
              <div className="aspect-[4/3] overflow-hidden mb-6">
                <img
                  src="https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=800"
                  alt="Casco Bay Ferry"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 p-4">
                <h4 className="font-medium text-amber-800 mb-2">Pro Tips</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Book your return ferry in advance during summer</li>
                  <li>• Arrive 15-20 minutes before departure</li>
                  <li>• Bring cash for snacks on the ferry</li>
                  <li>• Take the ferry arriving before 3 PM for a 4 PM check-in</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parking Information */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="md:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#134E4A] text-white flex items-center justify-center">
                  <Car size={24} />
                </div>
                <h2 className="font-serif text-3xl text-[#1C1917]">Parking in Portland</h2>
              </div>
              
              <p className="text-[#78716C] mb-6">
                Since Long Island is car-free (aside from island vehicles), you will need to park your car on the mainland. Several options are available near the ferry terminal.
              </p>

              <div className="space-y-4">
                <div className="bg-[#FAFAF9] p-4">
                  <h4 className="font-medium text-[#1C1917] mb-2">Casco Bay Lines Parking</h4>
                  <p className="text-sm text-[#78716C]">Located at the ferry terminal. Daily rates apply (~$15/day). Can fill up quickly in summer.</p>
                </div>
                <div className="bg-[#FAFAF9] p-4">
                  <h4 className="font-medium text-[#1C1917] mb-2">Ocean Gateway Garage</h4>
                  <p className="text-sm text-[#78716C]">Short walk to ferry terminal. Covered parking available. Slightly higher rates but more availability.</p>
                </div>
                <div className="bg-[#FAFAF9] p-4">
                  <h4 className="font-medium text-[#1C1917] mb-2">Street Parking</h4>
                  <p className="text-sm text-[#78716C]">Limited metered parking available in the Old Port area. Not recommended for multi-day stays.</p>
                </div>
              </div>
            </div>

            <div className="md:order-1">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=800"
                  alt="Portland Maine Waterfront"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Portland */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-serif text-3xl text-[#1C1917] text-center mb-12">Getting to Portland, Maine</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-stone-200 p-6">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">By Air</h3>
              <p className="text-[#78716C] text-sm mb-4">
                Portland International Jetport (PWM) is the closest airport, just 15 minutes from the ferry terminal.
              </p>
              <p className="text-[#78716C] text-sm">
                Boston Logan Airport (BOS) is 2 hours south, with more flight options.
              </p>
            </div>
            <div className="bg-white border border-stone-200 p-6">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">By Car</h3>
              <p className="text-[#78716C] text-sm mb-4">
                From Boston: ~2 hours via I-95 North<br />
                From New York: ~5.5 hours via I-95 North
              </p>
              <p className="text-[#78716C] text-sm">
                GPS: 56 Commercial St, Portland, ME 04101
              </p>
            </div>
            <div className="bg-white border border-stone-200 p-6">
              <h3 className="font-serif text-xl text-[#1C1917] mb-4">By Train/Bus</h3>
              <p className="text-[#78716C] text-sm mb-4">
                Amtrak Downeaster runs from Boston to Portland (2.5 hours). Station is a short taxi ride from the ferry.
              </p>
              <p className="text-[#78716C] text-sm">
                Concord Coach Lines offers bus service from Boston and NYC.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#134E4A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Ready to Start Your Island Getaway?
          </h2>
          <p className="text-white/80 mb-8">
            Book your stay and we will help coordinate your arrival
          </p>
          <Link to="/booking">
            <Button className="bg-white text-[#134E4A] hover:bg-stone-100 px-8 py-6">
              Book Your Stay <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h3 className="font-serif text-2xl mb-2">Chestnut Hill Inn</h3>
          <p className="text-white/60 mb-4">209 Beach Ave, Long Island, ME</p>
          <div className="flex justify-center gap-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
